import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/auth-context';
import { harambotService, GuildConfig, LeagueInfo, HarambotApiError } from '../services/harambot-service';
import { discordService } from '../services/discord-service';
import { yahooService } from '../services/yahoo-service';

const GuildConfigPage = () => {
  const { guildId } = useParams<{ guildId: string }>();
  const navigate = useNavigate();
  const { guilds, accessToken, isAuthenticated, isYahooAuthenticated, isLoading: authLoading, loginYahoo } = useAuth();
  
  const [config, setConfig] = useState<GuildConfig | null>(null);
  const [leagues, setLeagues] = useState<LeagueInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guild = guilds.find(g => g.id === guildId);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
      return;
    }

    const fetchConfig = async () => {
      if (!guildId || !accessToken) return;
      
      setLoading(true);
      setError(null);
      try {
        const guildConfig = await harambotService.getGuildConfig(guildId, accessToken);
        setConfig(guildConfig);
        if (guildConfig.yahoo_authenticated) {
          loginYahoo();
        }

        try {
          if (harambotService.getLeagues) {
            setLoadingLeagues(true);
            const res = await harambotService.getLeagues(guildId, accessToken);
            const leaguesList = Array.isArray(res)
              ? res
              : (res as any)?.leagues && Array.isArray((res as any).leagues)
              ? (res as any).leagues
              : res
              ? [res]
              : [];
            setLeagues(leaguesList);
          }
        } catch (leagueErr) {
          console.error('Failed to load leagues:', leagueErr);
        } finally {
          setLoadingLeagues(false);
        }
      } catch (err) {
        console.error(err);
        const apiError = err as HarambotApiError;
        if (apiError?.status >= 500 && apiError?.status < 600) {
          setError('Failed to load guild configuration.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchConfig();
    }
  }, [guildId, accessToken, isAuthenticated, authLoading, navigate, loginYahoo]);

  const handleYahooLogin = () => {
    if (guildId) {
      window.location.href = yahooService.getOAuthUrl(guildId);
    }
  };

  const handleLeagueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selectedLeague = leagues.find(l => (l.league_id || l.id) === selectedId);
    setConfig(prev => {
      if (!prev) return null;
      const updated = { ...prev, league_id: selectedId };
      if (selectedLeague?.league_type && ['nfl', 'nba', 'mlb'].includes(selectedLeague.league_type)) {
        updated.league_type = selectedLeague.league_type as 'nfl' | 'nba' | 'mlb';
      }
      return updated;
    });
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guildId || !config || !accessToken) return;

    setSubmitting(true);
    setError(null);
    try {
      await harambotService.updateGuildConfig(guildId, config, accessToken);
      navigate('/profile');
    } catch (err: any) {
      setError('Failed to update guild configuration.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || (loading && !error)) {
    return <div className="page-container"><h1>Loading...</h1></div>;
  }

  if (!guild) {
    return (
      <div className="container profile-page">
        <div className="error-message">Server not found or you don't have permission to manage it.</div>
        <button onClick={() => navigate('/profile')} className="btn btn-secondary">Back to Profile</button>
      </div>
    );
  }

  console.info(`Initiating Yahoo OAuth flow for guild ${guildId} and url: ${yahooService.getOAuthUrl(guildId)}`);  
  return (
    <div className="container profile-page">
      <div className="user-profile-header">
        {guild.icon && (
          <img 
            src={discordService.getGuildIconUrl(guild.id, guild.icon)} 
            alt={guild.name} 
            className="user-avatar"
          />
        )}
        <div className="user-details">
          <h1>Configure {guild.name}</h1>
          <p>Update your Harambot settings for this server.</p>
        </div>
      </div>

      <section className="fantasy-providers-section" style={{ marginBottom: '40px' }}>
        <h2 className="section-title" style={{ fontSize: '1.5rem', marginBottom: '20px', borderBottom: '1px solid #333', paddingBottom: '10px' }}>Fantasy Provider Integration</h2>
        <div className="provider-cards">
          <div className="provider-card" style={{ background: '#1a1a1a', padding: '20px', borderRadius: '8px', border: '1px solid #333' }}>
            <h3 style={{ marginTop: 0 }}>Yahoo Fantasy</h3>
            {isYahooAuthenticated ? (
              <div className="status-badge connected" style={{ color: '#4caf50', fontWeight: 'bold' }}>✓ Connected</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p>Connect your Yahoo account to enable fantasy features for this server.</p>
                <button onClick={handleYahooLogin} className="btn btn-yahoo" style={{ alignSelf: 'flex-start' }}>
                  Connect Yahoo Account
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="config-form-container">
        {error && <p className="error-message">{error}</p>}
        
        <form onSubmit={handleSaveConfig}>
          <div className="form-group">
            <label htmlFor="league_id">Yahoo League ID</label>
            <select 
              id="league_id"
              value={config?.league_id || ''}
              onChange={handleLeagueChange}
              required
            >
              <option value="">
                {loadingLeagues ? 'Loading leagues...' : 'Select a league'}
              </option>
              {config?.league_id && !leagues.some(l => (l.league_id || l.id) === config.league_id) && (
                <option value={config.league_id}>{config.league_id}</option>
              )}
              {leagues.map((league) => {
                const id = league.league_id || league.id || '';
                const name = league.name || league.league_name || id;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="league_type">League Type</label>
            <select 
              id="league_type"
              value={config?.league_type || 'nfl'}
              onChange={(e) => setConfig(prev => prev ? {...prev, league_type: e.target.value as any} : null)}
              required
            >
              <option value="nfl">NFL</option>
              <option value="nba">NBA</option>
              <option value="mlb">MLB</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="RIP_text">RIP Text (Optional)</label>
            <input 
              type="text" 
              id="RIP_text"
              value={config?.RIP_text || ''}
              onChange={(e) => setConfig(prev => prev ? {...prev, RIP_text: e.target.value} : null)}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="RIP_image_url">RIP Image URL (Optional)</label>
            <input 
              type="text" 
              id="RIP_image_url"
              value={config?.RIP_image_url || ''}
              onChange={(e) => setConfig(prev => prev ? {...prev, RIP_image_url: e.target.value} : null)}
            />
          </div>
          
          <div className="form-actions" style={{ display: 'flex', gap: '15px', marginTop: '40px' }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/profile')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuildConfigPage;
